/**
 * Parse a shooter's registered equipment-belt configuration.
 *
 * The belt layout is stored on the shooter as a JSON string under
 * `extraFields.equipment` (set via the EquipmentBelt component at
 * registration). Returns the parsed object only when it actually contains
 * a holster, pouch, or magnet — otherwise null, so callers can simply do
 * `{belt && <EquipmentBelt ... />}` and render nothing for shooters (or
 * matches) that don't use the belt.
 *
 * @param {object} shooter
 * @returns {{holster?: string|number|null, pouches?: number[], magnets?: number[]}|null}
 */
export function parseEquipmentBelt(shooter) {
  const raw = shooter?.extraFields?.equipment;
  if (!raw) return null;
  let obj;
  try {
    obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const hasHolster = obj.holster != null && obj.holster !== '';
  const hasPouches = Array.isArray(obj.pouches) && obj.pouches.length > 0;
  const hasMagnets = Array.isArray(obj.magnets) && obj.magnets.length > 0;
  return hasHolster || hasPouches || hasMagnets ? obj : null;
}

// Gun-related registration fields, in display order, with their i18n label keys.
const GUN_FIELDS = [
  { key: 'gun_make_and_model', label: 'shooter.gunMakeAndModel' },
  { key: 'gun_serial', label: 'shooter.gunSerial' },
  { key: 'gun_serial_2', label: 'shooter.gunSerial2' },
  { key: 'shooter_gun_license', label: 'shooter.gunLicense' },
  { key: 'shooter_gun_license_exp', label: 'shooter.gunLicenseExp' },
];

/**
 * Collect the gun information a shooter entered at registration (make/model,
 * serials, license), for display during the equipment check. Values live in
 * `extraFields` under either the base key or the `_optional` variant depending
 * on how the field was configured. Returns only the fields that have a value.
 *
 * @param {object} shooter
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function getGunInfo(shooter) {
  const ef = shooter?.extraFields || {};
  const out = [];
  for (const { key, label } of GUN_FIELDS) {
    const raw = ef[key] ?? ef[`${key}_optional`];
    if (raw != null && String(raw).trim() !== '') {
      out.push({ key, label, value: String(raw) });
    }
  }
  return out;
}
